import React, { useState, useEffect } from 'react';
import { X, Save, Calendar, User, MapPin, DollarSign, Users as UsersIcon, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

const EventForm = ({ event, onClose, onSave, apiKey }) => {
  const [formData, setFormData] = useState({
    client_name: '',
    client_phone: '',
    client_email: '',
    event_type: '',
    is_multi_day: false,
    event_days: [],
    event_date: '',
    event_time: '',
    event_location: '',
    event_city: 'Lubumbashi',
    package_type: '',
    total_amount: 0,
    deposit_paid: 0,
    balance_due: 0,
    payment_status: 'pending',
    photographers: [],
    videographers: [],
    photographer: '',
    videographer: '',
    status: 'pending',
    delivery_status: 'not_started',
    notes: '',
    internal_notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (event) {
      setFormData(event);
    }
  }, [event]);

  // Calculate balance automatically
  useEffect(() => {
    const balance = formData.total_amount - formData.deposit_paid;
    setFormData(prev => ({ ...prev, balance_due: Math.max(0, balance) }));
    
    // Update payment status
    if (formData.deposit_paid >= formData.total_amount && formData.total_amount > 0) {
      setFormData(prev => ({ ...prev, payment_status: 'paid' }));
    } else if (formData.deposit_paid > 0) {
      setFormData(prev => ({ ...prev, payment_status: 'partial' }));
    } else {
      setFormData(prev => ({ ...prev, payment_status: 'pending' }));
    }
  }, [formData.total_amount, formData.deposit_paid]);

  // Auto-fill amount when package is selected
  useEffect(() => {
    if (formData.package_type) {
      const packagePrices = {
        // Mariage packages
        'Pack Basique': 400,
        'Pack Standard': 500,
        'Pack Pro': 700,
        'Pack VIP': 1000,
        // Dot packages
        'Pack Basique Dot': 130,
        'Pack Standard Dot': 200,
        'Pack Pro Dot': 320,
        // Common
        'Personnalisé': 0
      };
      
      const price = packagePrices[formData.package_type];
      // Auto-fill for all packages including Personnalisé (which resets to 0)
      if (price !== undefined) {
        setFormData(prev => ({ ...prev, total_amount: price }));
      }
    }
  }, [formData.package_type]);

  // Handle multi-day toggle
  const handleMultiDayToggle = (isMulti) => {
    if (isMulti) {
      // Initialize with 1 day
      setFormData(prev => ({
        ...prev,
        is_multi_day: true,
        event_days: [{
          day_number: 1,
          event_date: prev.event_date || '',
          event_time: prev.event_time || '',
          event_location: prev.event_location || ''
        }]
      }));
    } else {
      // Switch back to single day
      const firstDay = formData.event_days[0];
      setFormData(prev => ({
        ...prev,
        is_multi_day: false,
        event_date: firstDay?.event_date || '',
        event_time: firstDay?.event_time || '',
        event_location: firstDay?.event_location || '',
        event_days: []
      }));
    }
  };

  // Add a new day
  const addDay = () => {
    if (formData.event_days.length < 7) {
      setFormData(prev => ({
        ...prev,
        event_days: [
          ...prev.event_days,
          {
            day_number: prev.event_days.length + 1,
            event_date: '',
            event_time: '',
            event_location: ''
          }
        ]
      }));
    }
  };

  // Remove a day
  const removeDay = (dayNumber) => {
    if (formData.event_days.length > 1) {
      setFormData(prev => ({
        ...prev,
        event_days: prev.event_days
          .filter(day => day.day_number !== dayNumber)
          .map((day, index) => ({
            ...day,
            day_number: index + 1
          }))
      }));
    }
  };

  // Update a specific day
  const updateDay = (dayNumber, field, value) => {
    setFormData(prev => ({
      ...prev,
      event_days: prev.event_days.map(day =>
        day.day_number === dayNumber
          ? { ...day, [field]: value }
          : day
      )
    }));
  };

  // Add a photographer
  const addPhotographer = () => {
    setFormData(prev => ({
      ...prev,
      photographers: [...prev.photographers, { name: '', phone: '' }]
    }));
  };

  // Remove a photographer
  const removePhotographer = (index) => {
    setFormData(prev => ({
      ...prev,
      photographers: prev.photographers.filter((_, i) => i !== index)
    }));
  };

  // Update a photographer
  const updatePhotographer = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      photographers: prev.photographers.map((photo, i) =>
        i === index ? { ...photo, [field]: value } : photo
      )
    }));
  };

  // Add a videographer
  const addVideographer = () => {
    setFormData(prev => ({
      ...prev,
      videographers: [...prev.videographers, { name: '', phone: '' }]
    }));
  };

  // Remove a videographer
  const removeVideographer = (index) => {
    setFormData(prev => ({
      ...prev,
      videographers: prev.videographers.filter((_, i) => i !== index)
    }));
  };

  // Update a videographer
  const updateVideographer = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      videographers: prev.videographers.map((video, i) =>
        i === index ? { ...video, [field]: value } : video
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const url = event 
        ? `${apiUrl}/api/events/${event.id}`
        : `${apiUrl}/api/events/`;
      
      const method = event ? 'PUT' : 'POST';

      // Debug logging
      console.log('=== EVENT FORM SUBMISSION ===');
      console.log('API Key présente:', apiKey ? 'OUI' : 'NON');
      console.log('API Key longueur:', apiKey?.length);
      console.log('API Key début:', apiKey ? apiKey.substring(0, 10) + '...' : 'N/A');
      console.log('URL:', url);
      console.log('Méthode:', method);
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify(formData)
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        // Get detailed error message from backend
        const errorData = await response.json().catch(() => null);
        
        console.error('Error response:', errorData);
        
        if (response.status === 401) {
          throw new Error(`Clé API invalide. Clé utilisée: ${apiKey ? apiKey.substring(0, 10) + '...' : 'AUCUNE'}. Veuillez vous déconnecter et reconnecter.`);
        } else if (response.status === 422 && errorData?.detail) {
          // Validation error - show specific field errors
          const errors = Array.isArray(errorData.detail) 
            ? errorData.detail.map(err => {
                const field = err.loc?.[1] || 'champ';
                const message = err.msg || 'erreur de validation';
                return `${field}: ${message}`;
              }).join(', ')
            : errorData.detail;
          throw new Error(`Erreur de validation: ${errors}`);
        } else if (errorData?.detail) {
          throw new Error(errorData.detail);
        } else {
          throw new Error(`Erreur ${response.status}: Impossible d'enregistrer l'événement`);
        }
      }

      const savedEvent = await response.json();
      console.log('Event saved successfully:', savedEvent.id);
      onSave(savedEvent);
      onClose();
    } catch (err) {
      console.error('Error submitting event:', err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const eventTypes = [
    'Mariage', 'Dot', 'Conférence', 'Concert', 
    'Anniversaire', 'Baptême', 'Funérailles',
    'Événement Gouvernemental', 'Festival', 'Autre'
  ];

  // Get packages based on event type
  const getPackages = () => {
    if (formData.event_type === 'Dot') {
      return [
        { name: 'Pack Basique Dot', price: 130 },
        { name: 'Pack Standard Dot', price: 200 },
        { name: 'Pack Pro Dot', price: 320 },
        { name: 'Personnalisé', price: 0 }
      ];
    } else {
      // Mariage and all other event types
      return [
        { name: 'Pack Basique', price: 400 },
        { name: 'Pack Standard', price: 500 },
        { name: 'Pack Pro', price: 700 },
        { name: 'Pack VIP', price: 1000 },
        { name: 'Personnalisé', price: 0 }
      ];
    }
  };

  const packages = getPackages();

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 overflow-y-auto">
      <Card className="w-full max-w-4xl my-8">
        <CardContent className="p-0">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-amber-600 text-white p-6 rounded-t-2xl flex justify-between items-center z-10">
            <h2 className="text-2xl font-bold">
              {event ? 'Modifier l\'événement' : 'Nouvel Événement'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {/* Client Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <User className="h-5 w-5 text-amber-600" />
                Informations Client
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du client *
                  </label>
                  <input
                    type="text"
                    required
                    minLength={2}
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Jean Mukendi"
                    title="Entrez le nom complet du client (minimum 2 caractères)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    required
                    minLength={10}
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="+243 999 000 000"
                    title="Entrez un numéro de téléphone valide (minimum 10 caractères)"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="client@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-amber-600" />
                Détails de l'Événement
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type d'événement *
                  </label>
                  <select
                    required
                    value={formData.event_type}
                    onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    title="Veuillez sélectionner un type d'événement"
                  >
                    <option value="">Sélectionner un type...</option>
                    {eventTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ville *
                  </label>
                  <select
                    required
                    value={formData.event_city}
                    onChange={(e) => setFormData({ ...formData, event_city: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="Lubumbashi">Lubumbashi</option>
                    <option value="Kolwezi">Kolwezi</option>
                  </select>
                </div>
              </div>

              {/* Multi-day Toggle */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_multi_day}
                    onChange={(e) => handleMultiDayToggle(e.target.checked)}
                    className="w-5 h-5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    Événement sur plusieurs jours (jusqu'à 7 jours)
                  </span>
                </label>
              </div>

              {/* Single Day Fields */}
              {!formData.is_multi_day && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.event_date}
                      onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Heure
                    </label>
                    <input
                      type="time"
                      value={formData.event_time}
                      onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lieu *
                    </label>
                    <input
                      type="text"
                      required
                      minLength={3}
                      value={formData.event_location}
                      onChange={(e) => setFormData({ ...formData, event_location: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="Adresse complète du lieu de l'événement"
                      title="Entrez l'adresse complète du lieu (minimum 3 caractères)"
                    />
                  </div>
                </div>
              )}

              {/* Multi-Day Fields */}
              {formData.is_multi_day && (
                <div className="space-y-4">
                  {formData.event_days.map((day, index) => (
                    <div key={day.day_number} className="bg-gray-50 p-4 rounded-lg space-y-3">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold text-gray-900">Jour {day.day_number}</h4>
                        {formData.event_days.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeDay(day.day_number)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Retirer
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date *
                          </label>
                          <input
                            type="date"
                            required
                            value={day.event_date}
                            onChange={(e) => updateDay(day.day_number, 'event_date', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Heure
                          </label>
                          <input
                            type="time"
                            value={day.event_time}
                            onChange={(e) => updateDay(day.day_number, 'event_time', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Lieu *
                          </label>
                          <input
                            type="text"
                            required
                            minLength={3}
                            value={day.event_location}
                            onChange={(e) => updateDay(day.day_number, 'event_location', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="Adresse complète du lieu"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {formData.event_days.length < 7 && (
                    <button
                      type="button"
                      onClick={addDay}
                      className="w-full py-2 border-2 border-dashed border-amber-400 rounded-lg text-amber-600 hover:bg-amber-50 font-medium transition-colors"
                    >
                      + Ajouter un jour
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Pricing */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-amber-600" />
                Package & Tarification
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Package *
                  </label>
                  <select
                    required
                    value={formData.package_type}
                    onChange={(e) => setFormData({ ...formData, package_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    title="Veuillez sélectionner un package"
                  >
                    <option value="">Sélectionner un package...</option>
                    {packages.map(pkg => (
                      <option key={pkg.name} value={pkg.name}>
                        {pkg.name} {pkg.price > 0 ? `- $${pkg.price}` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Le montant se remplit automatiquement
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant Total (USD) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.total_amount}
                    onChange={(e) => setFormData({ ...formData, total_amount: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    title="Entrez le montant total de l'événement"
                    placeholder="Ex: 500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Acompte Payé (USD)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.deposit_paid}
                    onChange={(e) => setFormData({ ...formData, deposit_paid: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Solde Restant (USD)
                  </label>
                  <input
                    type="number"
                    value={formData.balance_due}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Statut Paiement :</strong> {
                    formData.payment_status === 'paid' ? '✅ Payé intégralement' :
                    formData.payment_status === 'partial' ? '⚠️ Acompte versé' :
                    '❌ Non payé'
                  }
                </p>
              </div>
            </div>

            {/* Team Assignment */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <UsersIcon className="h-5 w-5 text-amber-600" />
                Équipe Assignée
              </h3>
              
              {/* Photographers Section */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-semibold text-gray-900">
                    Photographes
                  </label>
                  <button
                    type="button"
                    onClick={addPhotographer}
                    className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                  >
                    + Ajouter un photographe
                  </button>
                </div>
                
                {formData.photographers.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-gray-500 text-sm">Aucun photographe assigné</p>
                    <button
                      type="button"
                      onClick={addPhotographer}
                      className="mt-2 text-amber-600 hover:text-amber-700 font-medium text-sm"
                    >
                      Ajouter le premier photographe
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.photographers.map((photo, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <input
                              type="text"
                              value={photo.name}
                              onChange={(e) => updatePhotographer(index, 'name', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                              placeholder="Nom du photographe"
                              required
                            />
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="tel"
                              value={photo.phone}
                              onChange={(e) => updatePhotographer(index, 'phone', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                              placeholder="+243 999 000 000"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => removePhotographer(index)}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Retirer"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Videographers Section */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-semibold text-gray-900">
                    Vidéastes
                  </label>
                  <button
                    type="button"
                    onClick={addVideographer}
                    className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                  >
                    + Ajouter un vidéaste
                  </button>
                </div>
                
                {formData.videographers.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-gray-500 text-sm">Aucun vidéaste assigné</p>
                    <button
                      type="button"
                      onClick={addVideographer}
                      className="mt-2 text-amber-600 hover:text-amber-700 font-medium text-sm"
                    >
                      Ajouter le premier vidéaste
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.videographers.map((video, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <input
                              type="text"
                              value={video.name}
                              onChange={(e) => updateVideographer(index, 'name', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                              placeholder="Nom du vidéaste"
                              required
                            />
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="tel"
                              value={video.phone}
                              onChange={(e) => updateVideographer(index, 'phone', e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                              placeholder="+243 999 000 000"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => removeVideographer(index)}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Retirer"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="h-5 w-5 text-amber-600" />
                Statuts & Notes
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut Événement
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="pending">En attente</option>
                    <option value="confirmed">Confirmé</option>
                    <option value="in_progress">En cours</option>
                    <option value="completed">Terminé</option>
                    <option value="delivered">Livré</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut Livraison
                  </label>
                  <select
                    value={formData.delivery_status}
                    onChange={(e) => setFormData({ ...formData, delivery_status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="not_started">Non commencé</option>
                    <option value="editing">En montage</option>
                    <option value="ready">Prêt</option>
                    <option value="delivered">Livré</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes Client
                  </label>
                  <textarea
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                    placeholder="Notes visibles par le client"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes Internes
                  </label>
                  <textarea
                    rows={3}
                    value={formData.internal_notes}
                    onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                    placeholder="Notes internes (non visibles par le client)"
                  />
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="flex-1"
                disabled={isSubmitting}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSubmitting ? 'Enregistrement...' : event ? 'Mettre à jour' : 'Créer l\'événement'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EventForm;
